/**
 * "The API is behind this console" — one banner, instead of ten per-screen catches.
 *
 * `apps/web` used to rebuild on every push to `main` while the api Worker was promoted by hand, so
 * for the whole stretch between a merge and a dispatch the console was NEWER than the API it talks
 * to, and every screen that reaches a route the Worker predates answers "not available on this
 * deployment yet". That sentence is correct and it is scattered: an administrator meets it under
 * Legal disclosure, again under Connect, again on a favourite star, and concludes something is
 * broken in several places rather than one thing being outstanding in one place.
 *
 * THAT ORDERING IS NOW REVERSED and this banner should be rare. `deploy-prod.yml` promotes the
 * Worker on every merge and only then advances the branch Pages builds the console from, so the API
 * leads and the console trails by a build (ADR 0113). What is left for this banner is the case
 * where a promotion FAILED: the console moves on, the API does not, and somebody should be told.
 * `releaseGap` therefore checks the direction rather than the mere difference — see its own note.
 *
 * This says the one thing once. Both sides stamp their commit — Pages sets `CF_PAGES_COMMIT_SHA`,
 * which `vite.config.ts` freezes as `VITE_CONSOLE_BUILD`; the deploy injects `BUILD_SHA`, which
 * `/_health` reports as `build` — and when the two differ, an owner or admin sees which release
 * each side is on and what closes the gap. A member sees nothing: they cannot dispatch a deploy,
 * and a banner they cannot act on is noise.
 *
 * ## Who is an admin, when the API is the thing that is old
 *
 * `GET /v1/profile` carries `permissions` from the same release that added this banner. A Worker
 * OLDER than that — which is the very situation the banner exists for — answers a profile with no
 * such field, and the console cannot tell a member from an owner. So absence falls back to one
 * probe: `GET /v1/organization/members?limit=1` is `members.manage`, which every admin and owner
 * holds and no member does. A 200 is an admin; anything else is not. The answer is discarded — the
 * only thing read is whether the door opened. Once every Worker in production carries the field
 * the probe never fires again, and it can be deleted.
 *
 * ## Once per session for a clean answer, once a minute for a bad one
 *
 * A CLEAN health answer is cached in `sessionStorage` and asked for once per browser session. A
 * BEHIND one is never cached and is re-asked every minute while the banner is up, because that is
 * the answer which changes: the promotion that closes the gap lands about ten minutes after the
 * merge that opened it. Caching every answer is what made this banner outlive its own subject — see
 * `rememberHealth` for the reading that reported it.
 *
 * **A cached answer belongs to the console build that saw it, and that is the second way this
 * banner has lied.** `sessionStorage` survives a reload, and a reload is exactly when the console
 * build can change — Pages rebuilds from `release` on every promotion, so the same tab loads a new
 * bundle while keeping the old cache. A clean answer is only clean RELATIVE to the console that
 * asked: cache `ee474c3` while the console is also `ee474c3`, come back to a console on `d49f601`,
 * and the same untouched value is now a gap. So the console build is stored WITH the answer and a
 * mismatch is no cache at all. See `apiHealth` for the reading, which is the whole diagnosis in one
 * line: the pair on screen had never been true at the same moment.
 *
 * A dismissal is remembered for the same pair of releases: the next deploy changes the pair, so a
 * new gap is announced afresh. Every storage access is in try/catch — a private window throws on
 * the accessor rather than returning null — and a browser that will not store simply asks again
 * on the next reload, which is the older behaviour and a correct one.
 *
 * `unknown` on EITHER side is "cannot tell", never a difference: dev, vitest, a preview build and a
 * Worker deployed by hand all report it, and none of them is a gap.
 */
import { useEffect, useState } from 'react'
import { Button, Callout } from '@flowza/ui'
import { api, apiConfigured } from '../../api/client'

/** This console's commit, or `unknown` (dev, vitest, any build outside Pages). */
export const CONSOLE_BUILD: string = import.meta.env.VITE_CONSOLE_BUILD ?? 'unknown'

/** When this console was built, frozen by `vite.config.ts`. Null in dev and under vitest. */
export const CONSOLE_BUILT_AT: string | null = import.meta.env.VITE_BUILD_TIME ?? null

/** The permission that marks an owner or admin — seeded `min admin` (20260901001300). */
const ADMIN_PERMISSION = 'organization.manage'

const HEALTH_KEY = 'flowza.release_gap.api_health'
const DISMISSED_KEY = 'flowza.release_gap.dismissed'

export type ReleaseGap = { readonly api: string; readonly console: string }

/** Seven characters, the way `git log --oneline` and `deploy-prod.yml` both name a commit. */
export const shortSha = (sha: string): string => sha.slice(0, 7)

/**
 * Whether the API is the OLDER of the two, which is the only direction worth a banner. Pure, so
 * the test can hold it to the edges: `unknown` or empty on either side is null; a prefix of the
 * other (a short SHA against a long one) is the same commit; case does not matter.
 *
 * ## Why direction, and why it did not used to matter
 *
 * Any difference used to mean "the API is behind", and that was true by construction: Pages built
 * the console from `main` in two minutes and the Worker was promoted by hand, so the console could
 * only ever be the newer side. Since the promotion started releasing the console itself — the
 * Worker first, then the branch Pages builds from — the other direction became the common one, for
 * the couple of minutes a Pages build takes. A difference alone would now announce "the API is
 * behind this console" in the window where the opposite is true.
 *
 * The two sides are not comparable by commit: the console holds two SHAs and no history, so it
 * cannot know which came first. They ARE comparable by clock. `/_health` reports `deployed_at` from
 * Cloudflare's version metadata, and `vite.config.ts` freezes the console's build time — so a
 * deployment stamped AFTER this bundle was built is the newer one, and this console is the stale
 * side. Nothing is broken in that direction: an older console asks for routes that exist. It gets
 * silence, for the same reason a member does — a banner nobody can act on is noise.
 *
 * When either clock is missing it reports the gap. That is the deliberate direction to fail in: the
 * harmful case is the one this banner exists for, and staying quiet about it to avoid a wrong
 * sentence would trade a visible defect for an invisible one.
 */
export function releaseGap(
  apiBuild: unknown,
  consoleBuild: string,
  apiDeployedAt: unknown = null,
  consoleBuiltAt: string | null = null,
): ReleaseGap | null {
  if (typeof apiBuild !== 'string') return null
  const a = apiBuild.trim().toLowerCase()
  const c = consoleBuild.trim().toLowerCase()
  if (a === '' || a === 'unknown' || c === '' || c === 'unknown') return null
  if (a === c || a.startsWith(c) || c.startsWith(a)) return null

  const deployed = typeof apiDeployedAt === 'string' ? Date.parse(apiDeployedAt) : NaN
  const built = typeof consoleBuiltAt === 'string' ? Date.parse(consoleBuiltAt) : NaN
  if (Number.isFinite(deployed) && Number.isFinite(built) && deployed > built) return null

  return { api: a, console: c }
}

const readSession = (key: string): string | null => {
  try {
    return window.sessionStorage.getItem(key)
  } catch {
    return null
  }
}

const writeSession = (key: string, value: string): void => {
  try {
    window.sessionStorage.setItem(key, value)
  } catch {
    // A browser that will not store asks again next time, which is what it did before.
  }
}

const pairKey = (gap: ReleaseGap): string => `${gap.api}:${gap.console}`

type ApiHealth = { readonly build: string; readonly deployedAt: string | null }

/**
 * What the deployment says it is, from the session cache or from `/_health`.
 *
 * Cached as JSON now that it carries two fields, under a key of its own: the previous key held a
 * bare SHA, and reading one of those back as JSON would throw inside a session that straddles this
 * release. A new name lets the old value expire with the session instead.
 *
 * ## Two reasons not to believe the cache, each from a banner that was false on screen
 *
 * **`consoleBuild` must match.** A stored answer was clean for the console that stored it, and
 * `sessionStorage` outlives that console: it survives the reload that swaps the bundle. Measured
 * from the founder's report — the banner read "the API is on release `ee474c3`; this console is on
 * `d49f601`", and `ee474c3` stopped being the deployed commit at 22:41Z on 2026-09-12 while
 * `d49f601` was not committed until 03:27Z on the 13th, with three promotions in between. The pair
 * had never been true at the same moment, which is the tell that one side was not read live. It was
 * this one: `ee474c3` was cached as CLEAN by a console built from `ee474c3`, and a later bundle read
 * it straight back. So the answer carries the build that asked, and an answer from another build is
 * no answer.
 *
 * No key rename is needed for the added field, unlike last time: an old payload parses fine and
 * simply has no `consoleBuild`, so it fails the same check and is refetched. Absence is a mismatch.
 *
 * **A re-check must not read a cache at all**, which is `useCache`. `look` asks again every minute
 * precisely to find out whether the answer has CHANGED, and answering that from a stored copy is
 * not a weaker check but an empty one. Today the stale value it would find is always rejected by
 * the check above, so this is belt to that braces — but the alternative is a re-check whose
 * correctness rests on "a behind answer is never written" staying true in another function.
 */
async function apiHealth(consoleBuild: string, useCache: boolean): Promise<ApiHealth> {
  const cached = useCache ? readSession(HEALTH_KEY) : null
  if (cached !== null) {
    try {
      const parsed: unknown = JSON.parse(cached)
      if (
        typeof parsed === 'object' &&
        parsed !== null &&
        'build' in parsed &&
        (parsed as { readonly consoleBuild?: unknown }).consoleBuild === consoleBuild
      ) {
        return parsed as ApiHealth
      }
    } catch {
      // Unreadable cache is no cache. Ask again.
    }
  }
  const health = await api<{ readonly build?: unknown; readonly deployed_at?: unknown }>('/_health')
  return {
    build: typeof health?.build === 'string' ? health.build : 'unknown',
    deployedAt: typeof health?.deployed_at === 'string' ? health.deployed_at : null,
  }
}

/**
 * Remember an answer — but ONLY a clean one, and this is the fix for a banner that outlived the
 * thing it described.
 *
 * `apiHealth` used to write every answer it fetched, on the reasoning that the deployment does not
 * change within a browser session. That was true while the Worker was promoted by hand and could
 * sit behind for days; it stopped being true the day promotion became automatic. The founder saw
 * the consequence: the API on `406f6eb`, the console on `c96c964`, and a promotion that had in fact
 * completed at 20:21 with the smoke step confirming `/_health` served the newer commit. The banner
 * was reading a value cached before the promotion and had no way to notice.
 *
 * A BEHIND answer is the one answer guaranteed to go stale, so it is the one never written. A clean
 * answer is safe to keep, because the API only moves forward and forward is silent (see
 * `releaseGap`). Between that and the re-check below, the banner cannot outlast the gap.
 *
 * "Safe to keep" was too strong by one word, and `apiHealth` records what it cost: safe to keep for
 * THIS console. Forward is silent only while the console stays put, and the console moves too — so
 * the build that asked is stored with the answer.
 */
const rememberHealth = (health: ApiHealth, consoleBuild: string): void =>
  writeSession(HEALTH_KEY, JSON.stringify({ ...health, consoleBuild }))

/** How often to ask again while a gap is on screen. A promotion takes about ten minutes. */
const RECHECK_MS = 60_000

/**
 * Whether the caller may act on the gap. The profile's `permissions` when the Worker sends it; the
 * `members.manage` probe when it does not (see the header). Any failure is "not an admin": the
 * banner fails CLOSED toward the quieter screen.
 */
async function callerIsAdmin(): Promise<boolean> {
  const profile = await api<{ readonly permissions?: unknown }>('/v1/profile')
  if (Array.isArray(profile?.permissions)) {
    return profile.permissions.includes(ADMIN_PERMISSION)
  }
  try {
    await api('/v1/organization/members?limit=1')
    return true
  } catch {
    return false
  }
}

/**
 * The banner. `consoleBuild` is a prop so a test can put a real build in front of it; the shell
 * passes nothing and gets the stamp.
 */
export function ReleaseGapBanner({
  consoleBuild = CONSOLE_BUILD,
  consoleBuiltAt = CONSOLE_BUILT_AT,
}: {
  readonly consoleBuild?: string
  readonly consoleBuiltAt?: string | null
}) {
  const [gap, setGap] = useState<ReleaseGap | null>(null)

  useEffect(() => {
    // Nothing to compare against: this build does not know its own commit, so no request is made
    // at all — the common case in dev and under every test that mounts the shell.
    if (!apiConfigured() || releaseGap('x', consoleBuild) === null) return
    let cancelled = false
    // Asked once however many times we look. Whether this person is an admin does not change
    // while they sit on the page, and two extra requests a minute for an answer we already have
    // would be the wrong way to fix a staleness bug.
    let admin: boolean | undefined
    // The session cache is worth one request on the first look and is the wrong thing to read on
    // every look after it — see `apiHealth`. Flipped BEFORE the await rather than after, so a first
    // look that throws does not leave the cache in play for every tick that follows it.
    let useCache = true

    const look = async (): Promise<void> => {
      const fromCache = useCache
      useCache = false
      const health = await apiHealth(consoleBuild, fromCache)
      const found = releaseGap(health.build, consoleBuild, health.deployedAt, consoleBuiltAt)
      if (cancelled) return

      if (found === null) {
        // Current. Worth remembering, and there is nothing left to watch for: the API only moves
        // forward from here and forward is silent.
        rememberHealth(health, consoleBuild)
        setGap(null)
        clearInterval(timer)
        return
      }
      if (readSession(DISMISSED_KEY) === pairKey(found)) return
      admin ??= await callerIsAdmin()
      if (!admin || cancelled) return
      setGap(found)
    }

    const swallow = () => {
      // A health check that could not be read is not a gap. The per-screen sentences still say
      // what they say; this banner only ever adds, never replaces.
    }

    // THE GAP CLOSES BY ITSELF NOW, usually within ten minutes of the merge that opened it, so a
    // banner that only ever appeared would be the same defect in the other direction: a warning
    // about a state that has passed, sitting there until the person reloads. Keep asking while it
    // is on screen, and take it down when it closes.
    //
    // The interval is created BEFORE the first look, so `look` can clear it whichever order things
    // resolve in. The other way round worked only because `look` awaits before its body reaches
    // the clear — true today, and not a thing to rest a cleanup on.
    const timer = setInterval(() => void look().catch(swallow), RECHECK_MS)
    void look().catch(swallow)

    return () => {
      cancelled = true
      clearInterval(timer)
    }
  }, [consoleBuild, consoleBuiltAt])

  if (gap === null) return null

  return (
    <div className="px-4 pt-4 sm:px-6 lg:px-8" role="status" data-testid="release-gap">
      <Callout tone="warning" title="The API is behind this console">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <p className="m-0 text-sm">
            The API is on release <code>{shortSha(gap.api)}</code>; this console is on{' '}
            <code>{shortSha(gap.console)}</code>. Some settings will answer &ldquo;not available on
            this deployment yet&rdquo; until the API catches up, which it does on its own after
            every merge. If this stays for long, a promotion has failed and there is an open issue
            for it.
          </p>
          <Button
            variant="secondary"
            onClick={() => {
              writeSession(DISMISSED_KEY, pairKey(gap))
              setGap(null)
            }}
          >
            Dismiss
          </Button>
        </div>
      </Callout>
    </div>
  )
}
